import React, { useCallback, useMemo } from "react";

import { InventorizationStatus } from "../../../entities/InventorizationStatus";
import { useInventorizationStatuses } from "../hooks/useInventorizationStatuses";
import type { ReadyConfig, ViewState } from "../shared/types";
import type { Place } from "../types/warehouse.types";
import { findPlaceByCode } from "../utils/inventorizationScan";
import { getRowId } from "../utils/placeUtils";
import { InventorizationSidePanel } from "./InventorizationSidePanel";
import { SidePanelRenderProps, ViewBase } from "./ViewBase";

/**
 * Strip virtual places (missing/additional) from the config — they are not physical,
 * so the inventorization tab should not render them, nor their area/row boundaries.
 */
const pruneVirtualPlaces = (readyConfig: ReadyConfig): ReadyConfig => {
  const placesEntries = Object.entries(readyConfig.places);

  // Drop an area/row boundary only when ALL its places are virtual
  const areaAllVirtual = new Map<string, boolean>();
  const rowAllVirtual = new Map<string, boolean>();
  for (const [, place] of placesEntries) {
    const isVirtual = Boolean(place.isVirtual);
    const rowName = `${place.areaName}${place.row}`;
    areaAllVirtual.set(place.areaName, (areaAllVirtual.get(place.areaName) ?? true) && isVirtual);
    rowAllVirtual.set(rowName, (rowAllVirtual.get(rowName) ?? true) && isVirtual);
  }

  return {
    ...readyConfig,
    places: Object.fromEntries(placesEntries.filter(([, place]) => !place.isVirtual)),
    areaBoundaries: readyConfig.areaBoundaries.filter((b) => !areaAllVirtual.get(b.name)),
    rowBoundaries: readyConfig.rowBoundaries.filter((b) => !rowAllVirtual.get(b.name)),
    rackBoundaries: readyConfig.rackBoundaries.filter((b) => !rowAllVirtual.get(b.rowName)),
    allRows: readyConfig.allRows.filter((name) => !rowAllVirtual.get(name)),
  };
};

const SIDE_PANEL_WIDTH = '60%';

interface InventorizationViewProps {
  readyConfig: ReadyConfig;
  terminalId: number;
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  urlSelectedPlaceIds: string[];
  hasPersistedView?: boolean;
}

export const InventorizationView: React.FC<InventorizationViewProps> = (props) => {
  const {
    statuses: inventorizationStatuses,
    createStatus,
    markPlaceEmpty,
    recordScannedPallet,
    deleteStatus,
    setPlaceStatusFromRecords,
    clearPlace,
  } = useInventorizationStatuses(props.terminalId);

  const placeModifier = useCallback(
    (place: Place): Place => {
      const statusData = inventorizationStatuses[place.id];
      const status = statusData?.status;
      if (status === InventorizationStatus.correct) {
        return { ...place, occupied: false, blocked: false };
      } else if (status === InventorizationStatus.incorrect) {
        return { ...place, occupied: true, blocked: false };
      } else {
        return { ...place, occupied: false, blocked: true };
      }
    },
    [inventorizationStatuses]
  );

  const prunedConfig = useMemo(() => pruneVirtualPlaces(props.readyConfig), [props.readyConfig]);

  return (
    <ViewBase
      {...props}
      readyConfig={prunedConfig}
      placeModifier={placeModifier}
      showSelectAll={false}
      sidePanelWidth={SIDE_PANEL_WIDTH}
      renderSidePanel={(sp: SidePanelRenderProps) => (
        <>
          <InventorizationSidePanel
            selectedPlaces={sp.selectedPlaces}
            terminalId={sp.terminalId}
            storageEntities={sp.storageEntities}
            placeAddressTemplates={props.readyConfig.placeAddressTemplates}
            // Search across every place, not only the visible ones: a hit that the
            // level/row filter hides is made visible instead of reported as missing.
            findPlace={(code) => findPlaceByCode(sp.allPlaces, code)}
            onPlaceSelect={(place) => {
              if (!sp.activeLevels.has(place.level)) sp.toggleLevel(place.level);
              const rowId = getRowId(place);
              if (!sp.activeRows.has(rowId)) sp.toggleRow(rowId);

              sp.setSelectedPlaceIds(new Set([place.place_id]));
            }}
            onPlaceDeselect={() => sp.setSelectedPlaceIds(new Set())}
            createStatus={createStatus}
            markPlaceEmpty={markPlaceEmpty}
            recordScannedPallet={recordScannedPallet}
            deleteStatus={deleteStatus}
            setPlaceStatusFromRecords={setPlaceStatusFromRecords}
            clearPlace={clearPlace}
          />
        </>
      )}
    />
  );
};
